import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { toast } from "sonner";
import { RefreshCw, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/common/DatePicker";
import { PageState } from "@/components/common/PageState";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import {
  useAnalyticsPreferences,
  useExportPlatformAnalyticsCsv,
  usePlatformAnalyticsSummary,
  useUpdateAnalyticsPreferences,
} from "@/hooks/useAdminAnalytics";
import {
  AdminAnalyticsPreferencesBlob,
  AnalyticsPeriod,
  PlatformAnalyticsSummaryFilters,
} from "@/types/admin-analytics";
import { downloadBlob } from "@/utils/downloadFile";
import { cn } from "@/lib/utils";
import { resolveCardOrder, resolveVisibleCards } from "./constants";
import { formatTimestamp } from "./utils/format";
import { useAnalyticsRefreshFlow } from "./hooks/useAnalyticsRefreshFlow";
import { InfrastructureHealthCard } from "./components/InfrastructureHealthCard";
import { ModuleUsageRankingCard } from "./components/ModuleUsageRankingCard";
import { PlatformKpiGrid } from "./components/PlatformKpiGrid";
import { PreferencesDialog } from "./components/PreferencesDialog";
import { ReportScheduleCard } from "./components/ReportScheduleCard";
import { StorageCapacityCard } from "./components/StorageCapacityCard";
import { TenantBreakdownTable } from "./components/TenantBreakdownTable";

export const PlatformAnalyticsDashboardPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<AnalyticsPeriod>("month");
  const [date, setDate] = useState<Date | null>(null);
  const [compare, setCompare] = useState(false);
  const [isPreferencesOpen, setPreferencesOpen] = useState(false);
  const { canAdminister } = usePlatformPermissions();
  const canManageAnalytics = canAdminister("analytics");

  const filters = useMemo<PlatformAnalyticsSummaryFilters>(
    () => ({
      period,
      date: date ? format(date, "yyyy-MM-dd") : undefined,
      compare,
    }),
    [period, date, compare],
  );

  const summaryQuery = usePlatformAnalyticsSummary(filters);
  const preferencesQuery = useAnalyticsPreferences();
  const updatePreferences = useUpdateAnalyticsPreferences();
  const exportCsv = useExportPlatformAnalyticsCsv();
  const { run, isRunning, isStatusLoading, isTriggering, triggerRefresh } =
    useAnalyticsRefreshFlow();

  const summary = summaryQuery.data;
  const preferences = preferencesQuery.data?.preferences;
  const cardOrder = useMemo(() => resolveCardOrder(preferences), [preferences]);
  const visibleCards = useMemo(
    () => resolveVisibleCards(preferences),
    [preferences],
  );

  const isRefreshDisabled = isRunning || isTriggering;
  const lastRefreshedLabel = summary?.lastRefreshedAt
    ? formatTimestamp(summary.lastRefreshedAt)
    : null;
  const hasNeverRefreshed =
    !!summary && summary.lastRefreshedAt === null && run === null;
  const isWindowEmpty =
    !!summary &&
    summary.lastRefreshedAt !== null &&
    summary.moduleUsage.length === 0 &&
    summary.byTenant.length === 0;

  const handleRefresh = () => {
    if (!canManageAnalytics) return;
    triggerRefresh({ period: filters.period, date: filters.date });
  };

  const handleExport = async () => {
    if (!summary || !canManageAnalytics) return;
    try {
      const blob = await exportCsv.mutateAsync({
        period: filters.period,
        date: filters.date,
      });
      // Mirrors the backend's Content-Disposition filename pattern.
      downloadBlob(
        blob,
        `platform-analytics-${summary.period}-${summary.windowStart}.csv`,
      );
      toast.success(t("analytics.platform.exportDownloaded"));
    } catch {
      // BaseService already surfaces the error toast.
    }
  };

  const handleSavePreferences = (blob: AdminAnalyticsPreferencesBlob) => {
    if (!canManageAnalytics) return;
    updatePreferences.mutate(blob, {
      onSuccess: () => {
        setPreferencesOpen(false);
        toast.success(t("analytics.platform.preferencesSaved"));
      },
    });
  };

  if ((summaryQuery.isLoading || isStatusLoading) && !summary) {
    return <PageState text={t("common.loading")} />;
  }

  const refreshButton = (
    <Button
      variant="new_default"
      onClick={handleRefresh}
      disabled={isRefreshDisabled}
      aria-busy={isRefreshDisabled}
    >
      <RefreshCw
        className={cn("size-4", isRefreshDisabled && "animate-spin")}
        aria-hidden="true"
      />
      {t("analytics.platform.refresh")}
    </Button>
  );

  return (
    <div className="flex flex-col gap-6 p-12">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">
            {t("analytics.platform.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("analytics.platform.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManageAnalytics && (
            <>
              <Button
                variant="new_outline"
                onClick={() => setPreferencesOpen(true)}
              >
                <SlidersHorizontal className="size-4" />
                {t("analytics.platform.preferences")}
              </Button>
              {refreshButton}
            </>
          )}
        </div>
      </div>

      {/* Freshness & run-status bar */}
      <div
        aria-live="polite"
        className="border-base-border bg-background flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
      >
        <span className="text-muted-foreground text-sm">
          {lastRefreshedLabel
            ? t("analytics.platform.lastRefreshed", {
                time: lastRefreshedLabel,
              })
            : t("analytics.platform.neverRefreshed")}
        </span>
        {run?.status === "RUNNING" && (
          <Badge variant="verified">
            <RefreshCw className="size-3 animate-spin" aria-hidden="true" />
            {t("analytics.platform.status.running")}
          </Badge>
        )}
        {run?.status === "COMPLETED" && (
          <Badge variant="success">
            {t("analytics.platform.status.completed")}
          </Badge>
        )}
        {run?.status === "FAILED" && (
          <Badge variant="new_destructive" title={run.error ?? undefined}>
            {t("analytics.platform.status.failed")}
          </Badge>
        )}
      </div>

      {/* Window controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-muted-foreground text-xs font-medium">
            {t("analytics.platform.controls.period")}
          </Label>
          <Select
            value={period}
            onValueChange={(value) => setPeriod(value as AnalyticsPeriod)}
          >
            <SelectTrigger size="sm" className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">
                {t("analytics.platform.controls.week")}
              </SelectItem>
              <SelectItem value="month">
                {t("analytics.platform.controls.month")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-muted-foreground text-xs font-medium">
            {t("analytics.platform.controls.date")}
          </Label>
          <DatePicker
            date={date}
            onDateChange={setDate}
            formatString="dd MMM yyyy"
            placeholder={t("analytics.platform.controls.today")}
          />
        </div>
        <div className="flex h-10 items-center gap-2">
          <Switch
            id="platform-analytics-compare"
            checked={compare}
            onCheckedChange={setCompare}
          />
          <Label htmlFor="platform-analytics-compare" className="text-sm">
            {t("analytics.platform.controls.compare")}
          </Label>
        </div>
        {summary && (
          <span className="text-muted-foreground ml-auto text-sm">
            {summary.windowStart} – {summary.windowEnd}
          </span>
        )}
      </div>

      {hasNeverRefreshed ? (
        /* Empty state — never refreshed and no run ever triggered */
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <h2 className="text-base font-semibold">
              {t("analytics.platform.empty.title")}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("analytics.platform.empty.body")}
            </p>
            {canManageAnalytics && refreshButton}
          </CardContent>
        </Card>
      ) : (
        <>
          {isWindowEmpty && (
            <p className="text-muted-foreground text-sm">
              {t("analytics.platform.windowEmptyHint")}
            </p>
          )}
          {summary && (
            <PlatformKpiGrid
              totals={summary.totals}
              delta={summary.delta}
              compare={compare}
              cardOrder={cardOrder}
              visibleCards={visibleCards}
            />
          )}
          {summary && (
            <ModuleUsageRankingCard moduleUsage={summary.moduleUsage} />
          )}
          {summary && (
            <TenantBreakdownTable
              byTenant={summary.byTenant}
              onExport={() => void handleExport()}
              isExporting={exportCsv.isPending}
              canExport={canManageAnalytics}
            />
          )}
          <StorageCapacityCard />
          <InfrastructureHealthCard />
        </>
      )}

      {/* Scheduled report emails (spec §9) — config is independent of rollup data */}
      <ReportScheduleCard canEdit={canManageAnalytics} />

      {summaryQuery.error && (
        <div className="text-destructive py-4 text-center">
          {t("analytics.platform.errorLoadingSummary")}
        </div>
      )}

      {canManageAnalytics && (
        <PreferencesDialog
          open={isPreferencesOpen}
          onOpenChange={setPreferencesOpen}
          preferences={preferences}
          onSave={handleSavePreferences}
          isSaving={updatePreferences.isPending}
        />
      )}
    </div>
  );
};
